import { useState, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Filters,
  Text,
  Pagination,
  InlineStack,
  Box,
  Spinner,
  Banner,
  Badge,
  ChoiceList,
  Autocomplete,
  Icon,
  useIndexResourceState,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listVehicles, getVehicleFilterOptions, type VehicleListParams } from '../api/vehicles';

const PAGE_SIZE = 50;

export function VehiclesList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [makeInput, setMakeInput] = useState('');
  const [model, setModel] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [boltPattern, setBoltPattern] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [offset, setOffset] = useState(0);

  const filterOpts = useQuery({
    queryKey: ['vehicle-filter-options', make || null],
    // Scope Model dropdown to currently-selected Make so the user sees only
    // relevant models. When Make is cleared, fall back to the full model list.
    queryFn: () => getVehicleFilterOptions(make || undefined),
    staleTime: 60_000,
  });

  const params: VehicleListParams = {
    q: search || undefined,
    year: year ? Number(year) : undefined,
    make: make || undefined,
    model: model || undefined,
    bolt_pattern: boltPattern || undefined,
    include_inactive: includeInactive || undefined,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vehicles', params],
    queryFn: () => listVehicles(params),
  });

  const vehicles = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const resourceName = { singular: 'vehicle', plural: 'vehicles' };
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(vehicles.map((v) => ({ id: String(v.id) })));

  const resetFilters = useCallback(() => {
    setSearch(''); setYear('');
    setMake(''); setMakeInput('');
    setModel(''); setModelInput('');
    setBoltPattern(''); setOffset(0);
  }, []);

  const makeOptions = (filterOpts.data?.makes ?? [])
    .filter((m) => !makeInput || m.make.toLowerCase().includes(makeInput.toLowerCase()) || m.make_slug.includes(makeInput.toLowerCase()))
    .slice(0, 50)
    .map((m) => ({ value: m.make_slug, label: `${m.make} (${m.count.toLocaleString()})` }));

  const modelOptions = (filterOpts.data?.models ?? [])
    .filter((m) => !modelInput || m.model.toLowerCase().includes(modelInput.toLowerCase()) || m.model_slug.includes(modelInput.toLowerCase()))
    .slice(0, 100)
    .map((m) => ({ value: m.model_slug, label: `${m.model} (${m.count.toLocaleString()})` }));

  const applied = [
    year && { key: 'year', label: `Year: ${year}`, onRemove: () => setYear('') },
    make && { key: 'make', label: `Make: ${make}`, onRemove: () => { setMake(''); setMakeInput(''); setModel(''); setModelInput(''); } },
    model && { key: 'model', label: `Model: ${model}`, onRemove: () => { setModel(''); setModelInput(''); } },
    boltPattern && { key: 'bp', label: `BP: ${boltPattern}`, onRemove: () => setBoltPattern('') },
    includeInactive && { key: 'inactive', label: 'Include inactive', onRemove: () => setIncludeInactive(false) },
  ].filter(Boolean) as Array<{ key: string; label: string; onRemove: () => void }>;

  return (
    <Page
      title="Vehicles"
      subtitle={`${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
      primaryAction={{ content: 'New vehicle', onAction: () => navigate('/vehicles/new') }}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" title="Failed to load vehicles" onDismiss={() => refetch()}>
            <p>{(error as Error).message}</p>
          </Banner>
        </Box>
      )}
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={search}
            queryPlaceholder="Search make, model…"
            onQueryChange={(v) => { setSearch(v); setOffset(0); }}
            onQueryClear={() => setSearch('')}
            onClearAll={resetFilters}
            appliedFilters={applied}
            filters={[
              {
                key: 'year',
                label: 'Year',
                shortcut: true,
                filter: (
                  <ChoiceList
                    title="Year"
                    titleHidden
                    choices={(filterOpts.data?.years ?? []).map((y) => ({ label: String(y), value: String(y) }))}
                    selected={year ? [year] : []}
                    onChange={(v) => { setYear(v[0] || ''); setOffset(0); }}
                  />
                ),
              },
              {
                key: 'make',
                label: 'Make',
                shortcut: true,
                filter: (
                  <Autocomplete
                    options={makeOptions}
                    selected={make ? [make] : []}
                    onSelect={(selected) => {
                      // Clearing Make also clears Model since the model list
                      // is scoped to the currently-selected make.
                      const next = selected[0] ?? '';
                      setMake(next);
                      if (next !== make) { setModel(''); setModelInput(''); }
                      setOffset(0);
                    }}
                    textField={
                      <Autocomplete.TextField
                        label="Make"
                        labelHidden
                        value={makeInput}
                        onChange={setMakeInput}
                        placeholder="Start typing a make…"
                        prefix={<Icon source={SearchIcon} />}
                        autoComplete="off"
                      />
                    }
                  />
                ),
              },
              {
                key: 'model',
                label: 'Model',
                shortcut: true,
                filter: (
                  <Autocomplete
                    options={modelOptions}
                    selected={model ? [model] : []}
                    onSelect={(selected) => { setModel(selected[0] ?? ''); setOffset(0); }}
                    textField={
                      <Autocomplete.TextField
                        label="Model"
                        labelHidden
                        value={modelInput}
                        onChange={setModelInput}
                        placeholder={make ? 'Start typing a model…' : 'Pick a make first, or type to search all models'}
                        prefix={<Icon source={SearchIcon} />}
                        autoComplete="off"
                      />
                    }
                  />
                ),
              },
              {
                key: 'bp',
                label: 'Bolt pattern',
                filter: (
                  <ChoiceList
                    title="Bolt pattern"
                    titleHidden
                    choices={(filterOpts.data?.bolt_patterns ?? []).map((b) => ({
                      label: `${b.bolt_pattern_canonical} (${b.count.toLocaleString()})`,
                      value: b.bolt_pattern_canonical,
                    }))}
                    selected={boltPattern ? [boltPattern] : []}
                    onChange={(v) => { setBoltPattern(v[0] || ''); setOffset(0); }}
                  />
                ),
              },
              {
                key: 'inactive',
                label: 'Include inactive',
                filter: <label><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /> Show soft-deleted rows</label>,
              },
            ]}
          />
        </Box>

        {isLoading ? (
          <Box padding="600"><InlineStack align="center"><Spinner /></InlineStack></Box>
        ) : (
          <IndexTable
            resourceName={resourceName}
            itemCount={vehicles.length}
            selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
            onSelectionChange={handleSelectionChange}
            headings={[
              { title: 'ID' },
              { title: 'Year' },
              { title: 'Make' },
              { title: 'Model' },
              { title: 'Bolt pattern' },
              { title: 'Active' },
            ]}
            selectable={false}
          >
            {vehicles.map((v, i) => (
              <IndexTable.Row
                id={String(v.id)}
                key={v.id}
                position={i}
                onClick={() => navigate(`/vehicles/${v.id}`)}
              >
                <IndexTable.Cell>{v.id}</IndexTable.Cell>
                <IndexTable.Cell>{v.year}</IndexTable.Cell>
                <IndexTable.Cell>{v.make}</IndexTable.Cell>
                <IndexTable.Cell>{v.model}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd">{v.bolt_pattern_canonical}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {v.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="critical">Inactive</Badge>}
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}

        <Box padding="400" borderBlockStartWidth="025" borderColor="border">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </Text>
            <Pagination
              hasPrevious={offset > 0}
              onPrevious={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              hasNext={offset + PAGE_SIZE < total}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </InlineStack>
        </Box>
      </Card>
    </Page>
  );
}
